/**
 * Safe formatting utilities for KPI values with null/undefined protection.
 * These functions ensure that undefined or null values don't break the UI.
 */

/**
 * Safely format a number as a locale string (e.g., 1000 -> "1,000").
 * Returns "0" if value is undefined, null, or NaN.
 * 
 * @param value - The value to format
 * @param locale - Optional locale string (defaults to 'en-US')
 * @returns Formatted string with thousands separator
 */
export function formatCount(
  value: number | null | undefined,
  locale: string = 'en-US'
): string {
  if (value === null || value === undefined) {
    return '0';
  }
  
  const num = Number(value);
  if (isNaN(num)) {
    return '0';
  }
  
  return num.toLocaleString(locale);
}

/**
 * Safely format a percentage value.
 * Returns "0%" if value is undefined, null, or NaN.
 * 
 * @param value - The percentage value (0-100)
 * @param decimals - Number of decimal places (defaults to 0)
 * @param locale - Optional locale string (defaults to 'en-US')
 * @returns Formatted string with % symbol
 */
export function formatPercentage(
  value: number | null | undefined,
  decimals: number = 0,
  locale: string = 'en-US'
): string {
  if (value === null || value === undefined) {
    return '0%';
  }
  
  const num = Number(value);
  if (isNaN(num)) {
    return '0%';
  }
  
  const formatted = num.toFixed(decimals);
  return `${Number(formatted).toLocaleString(locale)}%`;
}

/**
 * Generic safe value accessor for KPI data with fallback.
 * 
 * @param value - The value to check
 * @param fallback - Fallback value if undefined/null (defaults to 0)
 * @returns The value or fallback
 */
export function safeValue(
  value: number | null | undefined,
  fallback: number = 0
): number {
  return value ?? fallback;
}

/**
 * Safe access to nested KPI field with deep value checking.
 * 
 * @param obj - The object to access
 * @param path - Dot notation path (e.g., 'data.stats.count')
 * @param fallback - Fallback value if not found
 * @returns The value or fallback
 */
export function getNestedValue(
  obj: any,
  path: string,
  fallback: number = 0
): number {
  try {
    const value = path.split('.').reduce((current, prop) => current?.[prop], obj);
    const numValue = Number(value);
    return isNaN(numValue) ? fallback : numValue;
  } catch {
    return fallback;
  }
}
